import {useEffect, useState} from 'react'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {Users, Plus, X} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {Badge} from '@/components/ui/badge'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Combobox, type ComboboxOption} from '@/components/ui/combobox'
import {DeleteConfirmDialog} from '@/components/shared/DeleteConfirmDialog'
import {CardSection} from '@/components/shared/CardSection'
import {FormField} from '@/components/shared/FormField'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {Icon} from '@/components/shared/Icon'
import {IconText} from '@/components/shared/IconText'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {useI18n} from '@/i18n'
import {labelOf, type LocalizedTypeValue} from '@/hooks/use-app-config'
import {useServices} from '@/services/context'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {formatDateOnly, AppInstant} from '@/lib/date-utils'
import type {OwnerRequest, OwnerResponse} from '@/types/owner'

/* eslint-disable i18next/no-literal-string -- enum-style constant for API path slugs */
export const OwnersEntityType = {
    systems: 'systems',
    resources: 'resources',
    accessProfiles: 'access-profiles',
    functionGroups: 'function-groups',
    restrictionRules: 'restriction-rules',
    autoAssignRules: 'auto-assign-rules',
    certificationPolicies: 'certification-policies',
    certificationCampaigns: 'certification-campaigns',
} as const
const OWNERS_QUERY_KEY = 'owners'
/* eslint-enable i18next/no-literal-string */
export type OwnersEntityType = typeof OwnersEntityType[keyof typeof OwnersEntityType]

export interface OwnersSectionProps {
    entityType: OwnersEntityType
    entityId: string
    canEdit: boolean
    /**
     * Per-entity responsibility vocabulary. Each call site reads its own
     * appConfig slot (target_system / resource / access_profile) and passes
     * the array in — the component stays free of entityType→appConfig
     * dispatching. See issue 931 concern B.
     */
    responsibilityOptions: LocalizedTypeValue[]
}

type OwnerRow = OwnerResponse

const ownerPartyId = (o: OwnerRow): string => o.ownerPartyId
const ownerPartyName = (o: OwnerRow): string => o.ownerPartyName
const ownerResponsibility = (o: OwnerRow): string => o.responsibility
const ownerValidFrom = (o: OwnerRow): string => o.validFrom
const ownerValidTo = (o: OwnerRow): string | null | undefined => o.validTo

/**
 * Shared owner-management widget for target systems and resources (issue 874).
 * Mirrors the {@link SourcesCard} pattern: parameterised by entityType +
 * entityId, owns its own query + mutations, gates edit actions on canEdit.
 * Consumed by {@code SystemDetail} (874) and {@code ResourceDetail} (875).
 */
export function OwnersSection({entityType, entityId, canEdit, responsibilityOptions}: OwnersSectionProps) {
    const i18n = useI18n()
    const services = useServices()
    const queryClient = useQueryClient()

    const ownersService = (() => {
        if (entityType === OwnersEntityType.systems) return services.systems
        if (entityType === OwnersEntityType.resources) return services.resources
        if (entityType === OwnersEntityType.functionGroups) return services.functionGroups
        if (entityType === OwnersEntityType.restrictionRules) return services.restrictionRules
        if (entityType === OwnersEntityType.autoAssignRules) return services.autoAssignRules
        if (entityType === OwnersEntityType.certificationPolicies) return services.certificationPolicies
        if (entityType === OwnersEntityType.certificationCampaigns) return services.certificationCampaigns
        return services.accessProfiles
    })()

    const defaultResponsibility =
        responsibilityOptions.find((o) => o.code === 'OWNER')?.code
        ?? responsibilityOptions[0]?.code
        ?? ''
    const singleOptionMode = responsibilityOptions.length <= 1

    const queryKey = [entityType, entityId, OWNERS_QUERY_KEY]

    const {data: allOwners = []} = useQuery<OwnerRow[]>({
        queryKey,
        queryFn: () => ownersService.owners(entityId) as Promise<OwnerRow[]>,
        enabled: !!entityId,
    })
    const owners = allOwners.filter((o) => o.isActive)

    const [addOpen, setAddOpen] = useState(false)
    const [partyId, setPartyId] = useState<string | null>(null)
    const [partySearchQuery, setPartySearchQuery] = useState('')
    const [responsibility, setResponsibility] = useState<string>(defaultResponsibility)

    // WHY: useAppConfig is async — at mount, defaultResponsibility resolves to
    // '' against the empty config; once the lookup loads, sync the responsibility
    // state so the Save button stops being blocked by an empty value (issue 883).
    useEffect(() => {
        if (!responsibility && defaultResponsibility) {
            setResponsibility(defaultResponsibility)
        }
    }, [defaultResponsibility, responsibility])
    const [removeOwnerRow, setRemoveOwnerRow] = useState<OwnerRow | null>(null)

    const partySearch = useQuery({
        queryKey: ['parties', 'search', partySearchQuery],
        queryFn: () => services.parties.list({q: partySearchQuery, size: 10}),
        enabled: addOpen,
    })
    const partyOptions: ComboboxOption[] = (partySearch.data?.content ?? []).map((p) => ({
        value: p.id,
        label: p.derivedDisplayName,
        description: p.partyType,
    }))

    const resetAddForm = () => {
        setAddOpen(false)
        setPartyId(null)
        setPartySearchQuery('')
        setResponsibility(defaultResponsibility)
    }

    const addMutation = useEntityMutation({
        mutationFn: (req: OwnerRequest): Promise<OwnerRow> =>
            ownersService.addOwner(entityId, req as never) as Promise<OwnerRow>,
        successMessage: i18n.owners.addedSuccess,
        onSuccess: () => {
            resetAddForm()
            queryClient.invalidateQueries({queryKey})
        },
    })

    const removeMutation = useEntityMutation({
        mutationFn: (ownerId: string) => ownersService.removeOwner(entityId, ownerId),
        successMessage: i18n.owners.removedSuccess,
        onSuccess: () => {
            setRemoveOwnerRow(null)
            queryClient.invalidateQueries({queryKey})
        },
    })

    const submitAdd = () => {
        if (!partyId || !responsibility.trim()) return
        addMutation.mutate({
            ownerPartyId: partyId,
            responsibility: responsibility.trim(),
            validFrom: AppInstant.now(),
            validTo: AppInstant.daysFromNow(365 * 100),
        })
    }

    return (
        <CardSection
            data-testid="section-owners"
            icon={Users}
            title={i18n.owners.title}
            description={canEdit ? i18n.owners.addOwnerDescription : undefined}
        >
            {responsibilityOptions.length === 0 && (
                <InfoBanner variant="destructive" data-testid="responsibility-lookup-missing-error">
                    {i18n.owners.responsibilityLookupMissing}
                </InfoBanner>
            )}
            {owners.length > 0 ? (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead data-testid="col-owner-party">{i18n.owners.party}</TableHead>
                            <TableHead data-testid="col-owner-responsibility">{i18n.owners.responsibility}</TableHead>
                            <TableHead data-testid="col-owner-valid-from">{i18n.owners.validFrom}</TableHead>
                            <TableHead data-testid="col-owner-valid-to">{i18n.owners.validTo}</TableHead>
                            {canEdit && <TableHead data-testid="col-owner-actions"/>}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {owners.map((owner) => (
                            <TableRow key={owner.id} data-testid={`row-owner-${ownerPartyId(owner)}`}>
                                <TableCell>{ownerPartyName(owner)}</TableCell>
                                <TableCell>
                                    <Badge variant="outline">{labelOf(responsibilityOptions, ownerResponsibility(owner))}</Badge>
                                </TableCell>
                                <TableCell>{formatDateOnly(ownerValidFrom(owner))}</TableCell>
                                <TableCell>{ownerValidTo(owner) ? formatDateOnly(ownerValidTo(owner)!) : '—'}</TableCell>
                                {canEdit && (
                                    <TableCell>
                                        <Button
                                            data-testid={`btn-remove-owner-${ownerPartyId(owner)}`}
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setRemoveOwnerRow(owner)}
                                        >
                                            <Icon icon={X}/>
                                        </Button>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <InlineEmptyText>{i18n.owners.empty}</InlineEmptyText>
            )}

            {canEdit && !addOpen && (
                <InlineRow>
                    <Button
                        data-testid="btn-add-owner"
                        size="sm"
                        variant="outline"
                        onClick={() => setAddOpen(true)}
                    >
                        <IconText icon={Plus}>{i18n.owners.addOwner}</IconText>
                    </Button>
                </InlineRow>
            )}

            {canEdit && addOpen && (
                <Stack gap="md" data-testid="add-owner-form" className="mt-4 p-4 rounded-lg border">
                    <FormField label={String(i18n.owners.party)} htmlFor="combobox-add-owner-party" required>
                        <Combobox
                            options={partyOptions}
                            value={partyId}
                            onValueChange={setPartyId}
                            onSearch={setPartySearchQuery}
                            placeholder={i18n.owners.searchParty}
                            isLoading={partySearch.isLoading}
                            data-testid="combobox-add-owner-party"
                        />
                    </FormField>
                    {!singleOptionMode && (
                        <FormField label={String(i18n.owners.responsibility)} htmlFor="select-add-owner-responsibility" required>
                            <Select value={responsibility} onValueChange={setResponsibility}>
                                <SelectTrigger
                                    id="select-add-owner-responsibility"
                                    data-testid="select-add-owner-responsibility"
                                >
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    {responsibilityOptions.map((opt) => (
                                        <SelectItem
                                            key={opt.code}
                                            value={opt.code}
                                            data-testid={`option-responsibility-${opt.code}`}
                                        >
                                            {labelOf(responsibilityOptions, opt.code)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormField>
                    )}
                    <InlineRow gap="sm">
                        <Button
                            data-testid="btn-cancel-add-owner"
                            type="button"
                            variant="outline"
                            onClick={resetAddForm}
                        >
                            {i18n.common.cancel}
                        </Button>
                        <Button
                            data-testid="btn-save-add-owner"
                            type="button"
                            disabled={!partyId || !responsibility.trim() || addMutation.isPending}
                            onClick={submitAdd}
                        >
                            {addMutation.isPending ? i18n.common.saving : i18n.common.save}
                        </Button>
                    </InlineRow>
                </Stack>
            )}

            <DeleteConfirmDialog
                open={!!removeOwnerRow}
                onOpenChange={(open) => !open && setRemoveOwnerRow(null)}
                title={i18n.owners.removeOwner}
                description={removeOwnerRow ? i18n.owners.removeConfirm({partyName: ownerPartyName(removeOwnerRow)}) : ''}
                testIdPrefix="remove-owner"
                isPending={removeMutation.isPending}
                onConfirm={() => removeOwnerRow && removeMutation.mutate(removeOwnerRow.id)}
            />
        </CardSection>
    )
}
